/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { memo } from "react";
import { useForm, FormProvider, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import Input from "components/Input";
import HeaderButtons from "components/Button/HeaderButtons";

export interface AddDocPayload {
  name: string;
  optional: boolean; // false = required, true = optional (API contract)
}

interface DocumentFormProps {
  onAdd: (data: AddDocPayload) => Promise<void>;
  adding?: boolean;
  onClose: () => void;
}

// .trim() is a yup transform — it strips leading/trailing whitespace before
// required() runs, so "   " is treated as "" and correctly fails validation.
const schema = yup.object().shape({
  name: yup
    .string()
    .trim()
    .required("Document name is required")
    .matches(/^[a-zA-Z0-9@#$%&()/-_, .]+$/, "Enter a valid document name"),
  isRequired: yup.boolean(),
});

// Modal form body for adding a new document.
// "isRequired" toggle in the UI maps to optional=false in the API payload (inverted).
// Rendered inside GenericModal in DocumentList — no modal wrapper here.
const DocumentForm: React.FC<DocumentFormProps> = ({ onAdd, adding, onClose }) => {
  const methods = useForm<any>({
    resolver: yupResolver(schema),
    defaultValues: { name: "", isRequired: false },
  });

  const { handleSubmit, reset, control, formState: { isDirty }, watch } = methods;
  const isRequired = watch("isRequired");
  const handleClose = () => {
    reset({ name: "", isRequired: false });
    onClose();
  };

  const onSubmit = async (data: any) => {
    await onAdd({ name: data.name.trim(), optional: !data.isRequired });
    reset({ name: "", isRequired: false });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5 p-1">
        {/* Document name — uses project-standard Input integrated with react-hook-form + Yup */}
        <Input name="name" label="Document Name" required={isRequired} compact />

        {/* Required toggle switch */}
        <div className="flex items-center justify-between py-1">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Required
          </span>
          <Controller
            name="isRequired"
            control={control}
            render={({ field }) => (
              <button
                type="button"
                onClick={() => field.onChange(!field.value)}
                disabled={adding}
                role="switch"
                aria-checked={!!field.value}
                aria-label="Toggle required"
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 ${
                  field.value ? "bg-blue-600" : "bg-gray-200 dark:bg-gray-600"
                } ${adding ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    field.value ? "translate-x-4" : "translate-x-0.5"
                  }`}
                />
              </button>
            )}
          />
        </div>

        {/* Form actions — Cancel + Submit */}
        <div className="flex items-center justify-end gap-3 pt-3 border-t border-gray-100 dark:border-gray-700">
          <HeaderButtons
            type="button"
            variant="tertiary"
            onClick={handleClose}
            disabled={adding}
          >
            Cancel
          </HeaderButtons>

          <HeaderButtons
            type="submit"
            variant="primary"
            loading={adding}
            disabled={adding || !isDirty}
          >
            Add Document
          </HeaderButtons>
        </div>
      </form>
    </FormProvider>
  );
};

export default memo(DocumentForm);
